/**
 * Conversion-focused campaign landing page configs.
 * Each page keeps unique problem/situation copy while sharing the same simple
 * prequalification funnel and compliant trust language.
 */

export const CAMPAIGN_PAGES = {
  "pret-auto": {
    slug: "pret-auto",
    entryIntent: null,
    fr: {
      badge: "PRÊT AUTO QUÉBEC",
      headline: "Explorez vos options de financement auto avant de choisir le véhicule.",
      sub: "Quelques questions rapides nous aident à comprendre votre situation, votre budget et le type de véhicule qui pourrait vous convenir.",
      problem: "Chercher un véhicule sans connaître ses options de financement peut mener à des visites inutiles et à des déceptions.",
      explanation: "Nous commençons par votre situation. Une fois vos options mieux comprises, notre équipe peut vous aider à trouver un véhicule d'occasion compatible chez un concessionnaire affilié.",
      trust: ["Moins de 2 minutes", "Sans obligation", "100 % confidentiel", "Partout au Québec"],
      faq: [
        ["Dois-je choisir mon véhicule avant de commencer?", "Non. Vous pouvez commencer par vérifier vos options de financement. Nous vous aiderons ensuite à trouver un véhicule adapté."],
        ["Est-ce que l'approbation est garantie?", "Non. Chaque dossier est analysé individuellement selon la situation, le budget et les critères disponibles."],
        ["Est-ce seulement pour les véhicules usagés?", "Oui. Les approbations concernent des véhicules d'occasion offerts chez nos concessionnaires affiliés."],
      ],
    },
    en: {
      badge: "QUEBEC AUTO LOAN",
      headline: "Explore your auto financing options before choosing the vehicle.",
      sub: "A few quick questions help us understand your situation, budget, and the type of vehicle that may fit.",
      problem: "Shopping for a vehicle without understanding your financing options can lead to wasted visits and disappointment.",
      explanation: "We start with your situation. Once your options are clearer, our team can help you find a compatible used vehicle at an affiliated dealership.",
      trust: ["Under 2 minutes", "No obligation", "100% confidential", "Across Quebec"],
      faq: [
        ["Do I need to choose my vehicle first?", "No. You can start by checking your financing options. We then help you find a suitable vehicle."],
        ["Is approval guaranteed?", "No. Every file is reviewed individually based on the situation, budget, and available criteria."],
        ["Is this only for used vehicles?", "Yes. Approvals apply to used vehicles offered through our affiliated dealerships."],
      ],
    },
  },

  "mauvais-credit": {
    slug: "mauvais-credit",
    entryIntent: "low-credit",
    fr: {
      badge: "CRÉDIT DIFFICILE",
      headline: "Une situation de crédit difficile mérite une analyse, pas un jugement.",
      sub: "Retards, R9 ou historique compliqué? Commencez par nous expliquer votre situation. Nous vérifions ensuite les options qui pourraient vous convenir.",
      problem: "Quand le crédit devient un obstacle, il est facile de croire qu'aucune solution n'existe. En réalité, chaque dossier a son contexte.",
      explanation: "RétablirMonCrédit regarde votre situation dans son ensemble — revenu, emploi, budget et objectif — avant de vous orienter vers les prochaines étapes.",
      trust: ["Approche respectueuse", "Analyse rapide", "Sans obligation", "Options avant véhicule"],
      faq: [
        ["Puis-je explorer mes options avec un historique difficile?", "Oui. Vous pouvez nous expliquer votre situation même si vous avez eu des retards, une R9 ou d'autres difficultés."],
        ["Est-ce que je dois connaître ma cote exacte?", "Non. Une estimation de votre situation suffit pour commencer."],
        ["Est-ce une promesse d'approbation?", "Non. Nous analysons votre situation et vous expliquons les options possibles, sans garantie."],
      ],
    },
    en: {
      badge: "CREDIT CHALLENGES",
      headline: "A difficult credit situation deserves a review, not judgment.",
      sub: "Late payments, R9 or a complicated history? Start by telling us your situation. We then check the options that may fit.",
      problem: "When credit becomes an obstacle, it is easy to believe no solution exists. In reality, every file has context.",
      explanation: "RétablirMonCrédit reviews your full situation — income, employment, budget and goal — before guiding you toward next steps.",
      trust: ["Respectful approach", "Fast review", "No obligation", "Options before vehicle"],
      faq: [
        ["Can I explore options with a difficult history?", "Yes. You can tell us about your situation even if you have late payments, an R9, or other challenges."],
        ["Do I need to know my exact score?", "No. An estimate of your situation is enough to start."],
        ["Is this an approval promise?", "No. We review your situation and explain possible options without a guarantee."],
      ],
    },
  },

  "refus-bancaire": {
    slug: "refus-bancaire",
    entryIntent: "bank-refusal",
    fr: {
      badge: "REFUS BANCAIRE",
      headline: "Votre banque vous a dit non? D'autres options peuvent exister.",
      sub: "Un refus ne raconte pas toute votre histoire. Parlez-nous de votre situation et nous vérifierons les solutions qui pourraient être adaptées.",
      problem: "Les banques utilisent souvent des critères très rigides. Un refus peut donner l'impression que votre projet est terminé.",
      explanation: "Nous prenons le temps de comprendre votre situation, votre revenu et votre budget afin d'explorer d'autres avenues possibles de financement.",
      trust: ["Analyse personnalisée", "Sans obligation", "100 % confidentiel", "Service au Québec"],
      faq: [
        ["Puis-je faire une demande après un refus bancaire?", "Oui. Un refus bancaire ne signifie pas nécessairement qu'aucune autre option n'existe."],
        ["Dois-je attendre avant de réessayer?", "Vous pouvez nous parler de votre situation dès maintenant. Nous vous dirons quelles informations sont utiles."],
        ["Est-ce que ma demande garantit une approbation?", "Non. Chaque situation est évaluée individuellement."],
      ],
    },
    en: {
      badge: "BANK REFUSAL",
      headline: "Your bank said no? Other options may exist.",
      sub: "A refusal does not tell your whole story. Tell us about your situation and we will check solutions that may fit.",
      problem: "Banks often use very rigid criteria. A refusal can make your project feel finished.",
      explanation: "We take time to understand your situation, income, and budget so we can explore other possible financing avenues.",
      trust: ["Personalized review", "No obligation", "100% confidential", "Quebec service"],
      faq: [
        ["Can I apply after a bank refusal?", "Yes. A bank refusal does not necessarily mean no other option exists."],
        ["Do I need to wait before trying again?", "You can tell us about your situation now. We will explain what information is useful."],
        ["Does my application guarantee approval?", "No. Every situation is reviewed individually."],
      ],
    },
  },

  "proposition-consommateur": {
    slug: "proposition-consommateur",
    entryIntent: "consumer-proposal",
    fr: {
      badge: "PROPOSITION DE CONSOMMATEUR",
      headline: "En proposition de consommateur? Votre projet automobile peut mériter une analyse.",
      sub: "Votre situation financière a changé. Commencez par nous expliquer où vous en êtes, sans jugement et sans obligation.",
      problem: "Une proposition de consommateur peut compliquer l'accès au financement, mais elle ne raconte pas toute votre réalité actuelle.",
      explanation: "Nous examinons votre revenu, votre stabilité et votre budget afin de déterminer si des options de financement peuvent être explorées.",
      trust: ["Sans jugement", "Analyse rapide", "100 % confidentiel", "Financement d'abord"],
      faq: [
        ["Puis-je faire une demande pendant une proposition?", "Vous pouvez nous parler de votre situation. Les options dépendent du dossier, du revenu et des conditions disponibles."],
        ["Ai-je besoin d'une mise de fonds?", "Pas nécessairement. Une mise de fonds peut aider dans certains cas, mais elle n'est pas une exigence pour commencer."],
        ["Dois-je déjà avoir choisi un véhicule?", "Non. Commencez par vérifier vos options de financement."],
      ],
    },
    en: {
      badge: "CONSUMER PROPOSAL",
      headline: "In a consumer proposal? Your vehicle project may still deserve a review.",
      sub: "Your financial situation changed. Start by telling us where you are today, without judgment or obligation.",
      problem: "A consumer proposal can make financing more complex, but it does not tell your whole current story.",
      explanation: "We review your income, stability, and budget to determine whether financing options can be explored.",
      trust: ["No judgment", "Fast review", "100% confidential", "Financing first"],
      faq: [
        ["Can I apply during a proposal?", "You can tell us about your situation. Options depend on the file, income, and available conditions."],
        ["Do I need a down payment?", "Not necessarily. A down payment can help in some cases, but it is not required to start."],
        ["Do I need to choose a vehicle first?", "No. Start by checking your financing options."],
      ],
    },
  },

  "faillite": {
    slug: "faillite",
    entryIntent: "bankruptcy",
    fr: {
      badge: "FAILLITE",
      headline: "Après une faillite, reprendre la route peut faire partie de votre nouveau départ.",
      sub: "Parlez-nous de votre situation actuelle. Nous vérifions les options qui pourraient vous aider à avancer, sans jugement.",
      problem: "Une faillite peut donner l'impression que le financement automobile est fermé pour longtemps. Ce n'est pas toujours aussi simple.",
      explanation: "Nous regardons où vous en êtes aujourd'hui — revenu, stabilité, budget et objectif — avant de vous guider vers les prochaines étapes.",
      trust: ["Nouveau départ", "Sans jugement", "Analyse rapide", "100 % confidentiel"],
      faq: [
        ["Puis-je explorer mes options après une faillite?", "Oui. Vous pouvez nous expliquer votre situation, que la faillite soit récente ou plus ancienne."],
        ["Est-ce que je dois connaître tous les détails de mon dossier?", "Non. Commencez avec ce que vous savez. Un spécialiste pourra vous guider ensuite."],
        ["Est-ce une approbation garantie?", "Non. Chaque dossier est évalué individuellement."],
      ],
    },
    en: {
      badge: "BANKRUPTCY",
      headline: "After bankruptcy, getting back on the road can be part of your fresh start.",
      sub: "Tell us about your current situation. We check the options that may help you move forward, without judgment.",
      problem: "Bankruptcy can make auto financing feel closed for a long time. That is not always the full picture.",
      explanation: "We look at where you are today — income, stability, budget and goal — before guiding you toward next steps.",
      trust: ["Fresh start", "No judgment", "Fast review", "100% confidential"],
      faq: [
        ["Can I explore options after bankruptcy?", "Yes. You can tell us about your situation, whether the bankruptcy is recent or older."],
        ["Do I need to know every detail of my file?", "No. Start with what you know. A specialist can guide you afterward."],
        ["Is approval guaranteed?", "No. Every file is reviewed individually."],
      ],
    },
  },

  "nouveau-canada": {
    slug: "nouveau-canada",
    entryIntent: "newcomer",
    fr: {
      badge: "NOUVEL ARRIVANT",
      headline: "Nouvel arrivant au Canada? Peu d'historique ne veut pas dire aucune option.",
      sub: "Expliquez-nous votre situation, votre emploi et vos besoins. Nous vous aidons à explorer les options de financement automobile possibles.",
      problem: "Sans long historique de crédit canadien, il peut être difficile de savoir par où commencer.",
      explanation: "Nous commençons par votre situation actuelle — revenu, emploi, budget et statut — puis nous vous orientons vers les prochaines étapes possibles.",
      trust: ["Approche humaine", "Nouveaux arrivants", "Sans obligation", "Partout au Québec"],
      faq: [
        ["Puis-je faire une demande sans historique canadien?", "Oui. Peu ou pas d'historique au Canada ne vous empêche pas de nous parler de votre situation."],
        ["Quels documents seront utiles?", "Un spécialiste pourra vous préciser les documents utiles selon votre statut, votre emploi et votre dossier."],
        ["Dois-je choisir le véhicule d'abord?", "Non. Vous pouvez commencer par vérifier vos options de financement."],
      ],
    },
    en: {
      badge: "NEWCOMER",
      headline: "New to Canada? Limited history does not mean no options.",
      sub: "Tell us about your situation, employment, and needs. We help you explore possible auto financing options.",
      problem: "Without a long Canadian credit history, it can be difficult to know where to start.",
      explanation: "We start with your current situation — income, employment, budget and status — then guide you toward possible next steps.",
      trust: ["Human approach", "Newcomers", "No obligation", "Across Quebec"],
      faq: [
        ["Can I apply without Canadian credit history?", "Yes. Little or no Canadian history does not prevent you from telling us about your situation."],
        ["Which documents will be useful?", "A specialist can clarify useful documents based on your status, employment, and file."],
        ["Do I need to choose the vehicle first?", "No. You can start by checking your financing options."],
      ],
    },
  },

  "travailleur-autonome": {
    slug: "travailleur-autonome",
    entryIntent: "self-employed",
    fr: {
      badge: "TRAVAILLEUR AUTONOME",
      headline: "Travailleur autonome? Votre revenu ne rentre pas toujours dans les cases traditionnelles.",
      sub: "Parlez-nous de votre réalité. Nous vérifions comment votre situation et vos revenus peuvent être présentés dans une demande de financement.",
      problem: "Les revenus variables ou non traditionnels peuvent rendre une demande plus difficile à présenter, même lorsque la situation est solide.",
      explanation: "Nous prenons le temps de comprendre votre activité, votre revenu et votre budget afin de préparer une analyse plus complète.",
      trust: ["Revenus non traditionnels", "Analyse humaine", "Sans obligation", "100 % confidentiel"],
      faq: [
        ["Puis-je faire une demande si mon revenu varie?", "Oui. Une fourchette de revenu suffit pour commencer. Un spécialiste pourra ensuite vous guider."],
        ["Dois-je fournir mes documents immédiatement?", "Non. La première étape sert à comprendre votre situation. Les documents utiles seront précisés ensuite."],
        ["Est-ce que mon statut autonome est un refus automatique?", "Non. Votre situation mérite une analyse individuelle."],
      ],
    },
    en: {
      badge: "SELF-EMPLOYED",
      headline: "Self-employed? Your income does not always fit traditional boxes.",
      sub: "Tell us about your reality. We review how your situation and income can be presented in a financing application.",
      problem: "Variable or non-traditional income can make an application harder to present, even when the situation is solid.",
      explanation: "We take time to understand your activity, income, and budget so we can prepare a fuller review.",
      trust: ["Non-traditional income", "Human review", "No obligation", "100% confidential"],
      faq: [
        ["Can I apply if my income varies?", "Yes. An income range is enough to start. A specialist can guide you afterward."],
        ["Do I need to provide documents immediately?", "No. The first step is understanding your situation. Useful documents will be clarified later."],
        ["Is self-employment an automatic refusal?", "No. Your situation deserves an individual review."],
      ],
    },
  },

  "reprise-vehicule-r9": {
    slug: "reprise-vehicule-r9",
    entryIntent: "repossession",
    fr: {
      badge: "REPRISE / R9",
      headline: "Reprise de véhicule ou R9? Commencez par expliquer votre situation.",
      sub: "Une difficulté passée ne doit pas vous empêcher de poser la question. Nous vérifions les options qui pourraient vous convenir aujourd'hui.",
      problem: "Une reprise ou une R9 peut rendre le financement plus complexe, mais votre situation actuelle compte aussi.",
      explanation: "Nous regardons votre revenu, votre stabilité et votre budget afin de déterminer les prochaines étapes possibles.",
      trust: ["Sans jugement", "Situation actuelle", "Analyse rapide", "100 % confidentiel"],
      faq: [
        ["Puis-je faire une demande après une reprise?", "Oui. Vous pouvez nous expliquer ce qui s'est passé et où vous en êtes aujourd'hui."],
        ["Dois-je avoir une mise de fonds?", "Pas nécessairement. Elle peut parfois aider, mais n'est pas une exigence pour commencer."],
        ["Est-ce une promesse d'approbation?", "Non. Chaque situation est analysée individuellement."],
      ],
    },
    en: {
      badge: "REPOSSESSION / R9",
      headline: "Vehicle repossession or R9? Start by explaining your situation.",
      sub: "A past challenge should not stop you from asking the question. We check the options that may fit today.",
      problem: "A repossession or R9 can make financing more complex, but your current situation matters too.",
      explanation: "We review your income, stability, and budget to determine possible next steps.",
      trust: ["No judgment", "Current situation", "Fast review", "100% confidential"],
      faq: [
        ["Can I apply after a repossession?", "Yes. You can explain what happened and where you are today."],
        ["Do I need a down payment?", "Not necessarily. It can sometimes help, but it is not required to start."],
        ["Is approval guaranteed?", "No. Every situation is reviewed individually."],
      ],
    },
  },

  "aucun-historique-credit": {
    slug: "aucun-historique-credit",
    entryIntent: "no-credit",
    fr: {
      badge: "AUCUN HISTORIQUE",
      headline: "Peu ou aucun historique de crédit? Commencez par vérifier vos options.",
      sub: "Vous n'avez pas besoin d'un long historique pour poser la question. Parlez-nous de votre situation actuelle.",
      problem: "Quand on n'a jamais emprunté, il peut être difficile de savoir comment une institution évaluera le dossier.",
      explanation: "Nous examinons votre revenu, votre emploi et votre budget afin d'identifier les prochaines étapes possibles pour un premier financement.",
      trust: ["Premier financement", "Analyse humaine", "Sans obligation", "100 % confidentiel"],
      faq: [
        ["Puis-je faire une demande sans historique?", "Oui. Vous pouvez commencer même si vous n'avez jamais eu de prêt ou de carte de crédit."],
        ["Est-ce qu'un co-signataire est obligatoire?", "Pas nécessairement. Cela dépend de votre situation et des options disponibles."],
        ["Dois-je choisir mon véhicule d'abord?", "Non. Commencez par vérifier vos options de financement."],
      ],
    },
    en: {
      badge: "NO CREDIT HISTORY",
      headline: "Little or no credit history? Start by checking your options.",
      sub: "You do not need a long history to ask the question. Tell us about your current situation.",
      problem: "When you have never borrowed, it can be difficult to know how an institution will review the file.",
      explanation: "We review your income, employment, and budget to identify possible next steps for a first financing experience.",
      trust: ["First financing", "Human review", "No obligation", "100% confidential"],
      faq: [
        ["Can I apply without credit history?", "Yes. You can start even if you have never had a loan or credit card."],
        ["Is a co-signer mandatory?", "Not necessarily. It depends on your situation and available options."],
        ["Do I need to choose my vehicle first?", "No. Start by checking your financing options."],
      ],
    },
  },

  "sans-comptant": {
    slug: "sans-comptant",
    entryIntent: "zero-down",
    fr: {
      badge: "SANS MISE DE FONDS",
      headline: "Aucune mise de fonds prévue? Commencez par vérifier vos options.",
      sub: "Une mise de fonds peut aider dans certains cas, mais elle n'est pas nécessaire pour poser la question.",
      problem: "Attendre d'avoir économisé peut retarder un projet important, surtout lorsque le véhicule est essentiel au quotidien.",
      explanation: "Nous commençons par comprendre votre budget, votre revenu et votre situation avant de vous dire quelles conditions pourraient être explorées.",
      trust: ["Mise de fonds non requise pour commencer", "Analyse rapide", "Sans obligation", "100 % confidentiel"],
      faq: [
        ["Puis-je commencer sans mise de fonds?", "Oui. Vous pouvez vérifier vos options même si vous ne prévoyez pas de mise de fonds."],
        ["Une mise de fonds peut-elle aider?", "Oui, dans certains cas elle peut améliorer les conditions ou le choix de véhicule."],
        ["Est-ce que 0 $ comptant est garanti?", "Non. Nous ne faisons aucune promesse de financement ou de conditions garanties."],
      ],
    },
    en: {
      badge: "NO DOWN PAYMENT",
      headline: "No down payment planned? Start by checking your options.",
      sub: "A down payment can help in some cases, but it is not required to ask the question.",
      problem: "Waiting to save can delay an important project, especially when a vehicle is essential every day.",
      explanation: "We start by understanding your budget, income, and situation before explaining which conditions may be explored.",
      trust: ["No down payment needed to start", "Fast review", "No obligation", "100% confidential"],
      faq: [
        ["Can I start without a down payment?", "Yes. You can check your options even if you are not planning a down payment."],
        ["Can a down payment help?", "Yes, in some cases it can improve conditions or vehicle choice."],
        ["Is $0 down guaranteed?", "No. We do not promise guaranteed financing or conditions."],
      ],
    },
  },
};

export const LOCAL_SERVICE_FAQS = {
  fr: [
    ["Le service est-il disponible près de chez moi?", "Oui. Nous servons les régions du Québec. La préqualification se fait en ligne; la finalisation se fait chez un concessionnaire affilié."],
    ["Puis-je choisir n'importe quel véhicule?", "Nous finançons uniquement des véhicules d'occasion offerts chez nos concessionnaires affiliés. Une fois votre admissibilité vérifiée, nous vous orientons vers les options disponibles."],
  ],
  en: [
    ["Is the service available near me?", "Yes. We serve regions across Quebec. Prequalification happens online; finalization happens at an affiliated dealership."],
    ["Can I choose any vehicle?", "We finance only used vehicles offered through our affiliated dealerships. Once your eligibility is checked, we direct you to available options."],
  ],
};

export const CAMPAIGN_SLUGS = Object.keys(CAMPAIGN_PAGES);
